/**
 * A dialog wrapped around a form: title, fields, cancel, submit.
 *
 * The part worth having in one place is the submit behaviour. Enter submits,
 * the button disables while the request is in flight, and **the failure lands
 * inside the dialog** rather than closing it — the alternative throws away
 * everything the user typed at the exact moment they need it back.
 */
import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { ErrorState } from './ErrorState';

export interface FormDialogProps {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  /** Defaults to the translated "Save". Pass one where the verb should be specific. */
  submitLabel?: string;
  /** Defaults to the translated "Cancel". */
  cancelLabel?: string;
  pending?: boolean;
  error?: Error | undefined;
  /** Blocks submit while the form is incomplete. */
  disabled?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md';
  onSubmit: () => void;
  onClose: () => void;
}

export function FormDialog({
  open,
  title,
  children,
  submitLabel,
  cancelLabel,
  pending = false,
  error,
  disabled = false,
  maxWidth = 'sm',
  onSubmit,
  onClose,
}: FormDialogProps) {
  const { t } = useTranslation('common');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!pending && !disabled) onSubmit();
  }

  return (
    <Dialog
      open={open}
      // A stray click outside must not discard a half-filled form mid-request.
      onClose={pending ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
    >
      {/* A real <form>, so Enter submits and browsers offer to fill it. */}
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {children}
            {error && <ErrorState error={error} severity="warning" />}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={pending}>
            {cancelLabel ?? t('action.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={pending || disabled}>
            {submitLabel ?? t('action.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
